import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Pause, Play, RotateCcw, User, Users, Headphones, Vibrate } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { playBell, chimeOn } from "@/lib/chime";
import { playVoice, stopVoice } from "@/lib/ariel";
import { fmtTime, localDay, vibrate } from "@/lib/quantum";
import { VoiceButton } from "@/components/quantum/TeachingCards";
import BreathCircle from "@/components/quantum/BreathCircle";
import BodyMap, { REGION_LABELS } from "@/components/quantum/BodyMap";
import EnergyMap from "@/components/app/EnergyMap";
import PracticeReflection from "@/components/app/PracticeReflection";
import { useSpace } from "@/lib/space";

const FIG_KEY = "qi-figure";
const pill = "inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-[13px] transition-colors";

export const StepCard = ({ step, k, total, partner = false, voice = null, children }) => (
  <motion.div key={k} initial={{ opacity: 0.3, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="mt-6 border border-[#D9B47A]/20 bg-[#0F070B] p-7">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#A8998C]">Step {k + 1} of {total}</p>
      {voice}
    </div>
    <h3 data-testid="qi-step-title" className="mt-4 font-display text-3xl leading-tight">{step.title}</h3>
    <p className="mt-4 text-[17px] leading-[1.75] text-[#DCCFC3]">{step.text}</p>
    <EnergyMap key={k} text={`${step.title}. ${step.text}`} partner={partner} testId="qi-energy-map" />
    {children}
  </motion.div>
);

export const Ring = ({ left, seconds }) => (
  <div className="relative w-20 h-20 shrink-0">
    <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
      <circle cx="20" cy="20" r="18" fill="none" stroke="#D9B47A" strokeOpacity="0.15" strokeWidth="1.5" />
      <circle cx="20" cy="20" r="18" fill="none" stroke="#E8838E" strokeWidth="1.5" strokeDasharray={113.1} strokeDashoffset={113.1 * (seconds ? left / seconds : 1)} strokeLinecap="round" className="transition-[stroke-dashoffset] duration-1000 ease-linear" />
    </svg>
    <span data-testid="qi-timer-left" className="absolute inset-0 flex items-center justify-center font-tt-mono text-[13px] text-[#F2E8DF]">{fmtTime(left)}</span>
  </div>
);

const Steps = ({ practice, handsFree, haptics, onFinish }) => {
  const [k, setK] = useState(0);
  const [left, setLeft] = useState(practice.steps[0]?.seconds ?? 0);
  const [running, setRunning] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const step = practice.steps[k];
  const last = k === practice.steps.length - 1;
  const advance = useRef(() => {});
  advance.current = () => (last ? onFinish() : setK((x) => x + 1));

  useEffect(() => {
    let cancelled = false;
    setLeft(step.seconds);
    setRunning(false);
    if (haptics) vibrate(40);
    if (handsFree) {
      setSpeaking(true);
      playVoice(`${step.title}. ${step.text}`).catch(() => {}).finally(() => {
        if (cancelled) return;
        setSpeaking(false);
        if (step.seconds) setRunning(true);
        else setTimeout(() => !cancelled && advance.current(), 1200);
      });
    }
    return () => {
      cancelled = true;
      stopVoice();
    };
  }, [k, step, handsFree, haptics]);

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [running]);

  useEffect(() => {
    if (!running || left > 0) return undefined;
    setRunning(false);
    if (chimeOn()) playBell();
    if (haptics) vibrate([60, 80, 60]);
    if (!handsFree) return undefined;
    const t = setTimeout(() => advance.current(), 1800);
    return () => clearTimeout(t);
  }, [left, running, handsFree, haptics]);

  return (
    <div data-testid="qi-practice-steps" className="mt-6">
      <div className="flex gap-1" aria-hidden>{practice.steps.map((_, j) => <span key={j} className={`h-[3px] flex-1 rounded-full ${j <= k ? "bg-[#E8838E]" : "bg-[#D9B47A]/15"}`} />)}</div>
      <StepCard step={step} k={k} total={practice.steps.length} partner={practice.mode === "partner"} voice={handsFree ? null : <VoiceButton text={`${step.title}. ${step.text}`} testId="qi-step-voice" />}>
        {handsFree && (
          <p data-testid="qi-handsfree-status" className="mt-6 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#E8838E]">{speaking ? "Ariel is guiding you…" : running ? "Stay with the breath" : "Moving on…"}</p>
        )}
        {step.seconds > 0 && (
          <>
            <div className="mt-8"><BreathCircle active={running} haptics={haptics} /></div>
            <div data-testid="qi-timer" className="mt-6 flex items-center gap-5">
              <Ring left={left} seconds={step.seconds} />
              <button data-testid="qi-timer-toggle" onClick={() => (left ? setRunning((r) => !r) : (setLeft(step.seconds), setRunning(true)))} className="inline-flex items-center gap-2 rounded-full border border-[#E8838E]/50 px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#E8838E] hover:bg-[#E8838E] hover:text-[#0A0706] transition-colors">
                {running ? <><Pause className="w-3.5 h-3.5" /> Pause</> : left ? <><Play className="w-3.5 h-3.5" /> {left === step.seconds ? "Begin timer" : "Resume"}</> : <><RotateCcw className="w-3.5 h-3.5" /> Again</>}
              </button>
            </div>
          </>
        )}
      </StepCard>
      <div className="mt-5 flex gap-3">
        <button data-testid="qi-step-prev" onClick={() => setK((x) => Math.max(0, x - 1))} disabled={k === 0} className="w-12 h-12 rounded-full border border-[#D9B47A]/30 flex items-center justify-center text-[#D9B47A] disabled:opacity-30" aria-label="Previous step"><ArrowLeft className="w-4 h-4" /></button>
        <button data-testid={last ? "qi-practice-finish" : "qi-step-next"} onClick={() => advance.current()} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] py-3.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors">
          {last ? <><Check className="w-4 h-4" /> Complete practice</> : <>Next step <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
};

// Names where tension eased between the before and after maps.
export const TensionShift = ({ before = {}, after = {} }) => {
  const eased = Object.keys(before).filter((k) => (before[k] ?? 0) > (after[k] ?? 0));
  const total = Object.values(before).reduce((n, v) => n + v, 0);
  const left = Object.values(after).reduce((n, v) => n + v, 0);
  if (!total) return null;
  return <p data-testid="tension-shift" className="mt-5 text-[15px] text-[#CFC2B6]">{eased.length ? <>Tension eased in <span className="text-[#9DB4D6]">{eased.map((k) => REGION_LABELS[k] ?? k).join(", ")}</span>{left ? "" : ", and has fully dissolved"}.</> : "Your tension is still held. Be gentle; it softens with time."}</p>;
};

const FieldCheck = ({ title, sub, figure, setFigure, value, onChange, tension, onTension, onNext, nextLabel, testId }) => (
  <div data-testid={testId} className="mt-6 border border-[#D9B47A]/20 p-6">
    <p className="font-display text-2xl">{title}</p>
    <p className="mt-1 text-[14px] text-[#A8998C]">{sub}</p>
    <div className="mt-5"><BodyMap figure={figure} onFigure={setFigure} value={value} onChange={onChange} tension={tension} onTension={onTension} testId={`${testId}-map`} /></div>
    <button data-testid={`${testId}-next`} onClick={onNext} className="mt-6 w-full rounded-full bg-[#D9B47A] text-[#0A0706] py-3.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors">{nextLabel}</button>
  </div>
);

// One practice: intro → map the field → steps (hands-free optional) → map again → saved to the practice log.
const Session = ({ g, practice, onExit }) => {
  const [phase, setPhase] = useState("intro");
  const [handsFree, setHandsFree] = useState(false);
  const [haptics, setHaptics] = useState(true);
  const [figure, setFigureState] = useState(() => localStorage.getItem(FIG_KEY) || "female");
  const [before, setBefore] = useState({});
  const [after, setAfter] = useState({});
  const [tBefore, setTBefore] = useState({});
  const [tAfter, setTAfter] = useState({});
  const [checkId, setCheckId] = useState(null);
  const space = useSpace();
  const setFigure = (f) => {
    localStorage.setItem(FIG_KEY, f);
    setFigureState(f);
  };
  const save = async () => {
    try {
      await api.post(`${space.api}/practice-log`, { g, practice: practice.title, day: localDay(), figure, before, after });
      api.post("/body/checks", { app: space.key, practice: practice.title, before: { current: before, tension: tBefore }, after: { current: after, tension: tAfter } })
        .then(({ data }) => setCheckId(data?.id ?? null)).catch(() => {});
      setPhase("saved");
    } catch {
      toast.error("Your practice couldn’t be saved just now.");
    }
  };
  const minutes = Math.max(1, Math.round(practice.steps.reduce((n, s) => n + s.seconds, 0) / 60));
  if (phase === "intro") {
    return (
      <div data-testid="qi-practice-intro" className="mt-6 mb-10 border border-[#D9B47A]/20 p-7">
        <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#E8838E]">{practice.mode === "partner" ? "With a partner" : "Solo practice"} · {practice.steps.length} steps · about {minutes} min</p>
        <h3 className="mt-4 font-display text-3xl leading-tight">{practice.title}</h3>
        <p className="mt-4 text-[16px] leading-relaxed text-[#CFC2B6]">{practice.intro}</p>
        <div className="mt-6 flex flex-wrap gap-2">
          <button data-testid="qi-handsfree-toggle" aria-pressed={handsFree} onClick={() => setHandsFree((v) => !v)} className={`${pill} ${handsFree ? "bg-[#E8838E] border-[#E8838E] text-[#0A0706]" : "border-[#E8838E]/40 text-[#E8838E]"}`}><Headphones className="w-4 h-4" /> Hands-free {handsFree ? "on" : "off"}</button>
          <button data-testid="qi-haptics-toggle" aria-pressed={haptics} onClick={() => setHaptics((v) => !v)} className={`${pill} ${haptics ? "border-[#D9B47A] text-[#D9B47A]" : "border-[#D9B47A]/30 text-[#A8998C]"}`}><Vibrate className="w-4 h-4" /> Vibration {haptics ? "on" : "off"}</button>
        </div>
        {handsFree && <p className="mt-3 text-[13px] text-[#A8998C]">Ariel reads each step aloud, the timer runs on its own, and she moves you on with a bell.</p>}
        <div className="mt-8 flex gap-3">
          <button data-testid="qi-practice-back" onClick={onExit} className="rounded-full border border-[#D9B47A]/30 px-5 py-3.5 text-[#D9B47A]" aria-label="Back to practices"><ArrowLeft className="w-4 h-4" /></button>
          <button data-testid="qi-practice-begin" onClick={() => setPhase("before")} className="flex-1 rounded-full bg-[#D9B47A] text-[#0A0706] py-3.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors">Begin</button>
        </div>
      </div>
    );
  }
  if (phase === "before") return <FieldCheck testId="qi-field-before" title="Before you begin" sub="Mark where you feel current, then switch to Tension and mark where you hold tension. Tap again to brighten. Or simply continue." figure={figure} setFigure={setFigure} value={before} onChange={setBefore} tension={tBefore} onTension={setTBefore} onNext={() => setPhase("steps")} nextLabel="Begin the practice" />;
  if (phase === "steps") return <Steps practice={practice} handsFree={handsFree} haptics={haptics} onFinish={() => setPhase("after")} />;
  if (phase === "after") return <FieldCheck testId="qi-field-after" title="Where is the current now?" sub="Map your current and your tension again, so you can watch tension dissolve over time." figure={figure} setFigure={setFigure} value={after} onChange={setAfter} tension={tAfter} onTension={setTAfter} onNext={save} nextLabel="Save this practice" />;
  return (
    <div data-testid="qi-practice-saved" className="mt-6 border border-[#D9B47A]/30 bg-[#140910] p-7">
      <p className="font-display italic text-2xl text-[#D9B47A]">Practice held. Your field remembers.</p>
      <div className="mt-6 grid grid-cols-2 gap-3">
        <div><p className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">Before</p><BodyMap figure={figure} value={before} tension={tBefore} testId="qi-saved-before" /></div>
        <div><p className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">After</p><BodyMap figure={figure} value={after} tension={tAfter} testId="qi-saved-after" /></div>
      </div>
      <TensionShift before={tBefore} after={tAfter} />
      <PracticeReflection checkId={checkId} testId="qi-practice-reflection" />
      <button data-testid="qi-practice-saved-done" onClick={() => onExit(true)} className="mt-6 w-full rounded-full border border-[#D9B47A]/40 py-3.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">Back to practices</button>
    </div>
  );
};

// The gate's guided solo and partner practices.
export default function GuidedPractice({ g, practices, onDone, initial = null }) {
  const [open, setOpen] = useState(initial);
  const [done, setDone] = useState([]);
  if (open !== null && practices[open]) {
    return <Session key={open} g={g} practice={practices[open]} onExit={(saved) => { if (saved) setDone((d) => [...new Set([...d, open])]); setOpen(null); }} />;
  }
  return (
    <div data-testid="qi-practices">
      <ul className="mt-6 space-y-3">
        {practices.map((p, k) => (
          <li key={k}>
            <button data-testid={`qi-practice-${k}`} onClick={() => setOpen(k)} className="w-full text-left flex items-center gap-4 border border-[#D9B47A]/20 p-5 hover:border-[#E8838E]/60 hover:bg-[#140910] transition-colors">
              <span className={`w-11 h-11 rounded-full border flex items-center justify-center shrink-0 ${done.includes(k) ? "bg-[#C2414F] border-[#C2414F] text-[#F2E8DF]" : "border-[#E8838E]/50 text-[#E8838E]"}`}>
                {done.includes(k) ? <Check className="w-4 h-4" /> : p.mode === "partner" ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />}
              </span>
              <span className="min-w-0">
                <span className="block font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">{p.mode === "partner" ? "Partner" : "Solo"} · {p.steps.length} steps</span>
                <span className="block mt-1 font-display text-xl leading-snug">{p.title}</span>
              </span>
              <ArrowRight className="ml-auto w-4 h-4 shrink-0 text-[#8F8176]" />
            </button>
          </li>
        ))}
      </ul>
      <button data-testid="qi-practices-done" onClick={onDone} className="mt-8 w-full rounded-full border border-[#D9B47A]/40 py-3.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">Continue to reflection</button>
    </div>
  );
}
